/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import toast, { Toaster } from "react-hot-toast";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserLock,
  faSearch,
  faShieldAlt,
  faExclamationTriangle,
  faRedoAlt,
  faTimes,
  faInfoCircle,
  faUserGraduate,
  faCheckCircle,
  faKey
} from "@fortawesome/free-solid-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import SchoolContext from "../../../Context/SchoolContext";
import AppContainer from "../../../Components/Structure/AppContainer";
import TableHeader from "../../../Components/Common/TableHeader";
import Loader from "../../../Components/Common/Loader";
import StudentContext from "../../../Context/StudentContext";
import ajaxStudent from "../../../util/remote/ajaxStudent";

function ViewSchoolStudentsPage() {
  const { schoolDetails } = useContext(SchoolContext);
  const { setSchoolId } = useContext(StudentContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchedQuery, setSearchedQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCharWarning, setShowCharWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  useEffect(() => {
    if (schoolDetails?.school_id) {
      setSchoolId(schoolDetails.school_id);
    }
  }, [schoolDetails?.school_id, setSchoolId]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    const trimmed = value.trim();
    if (trimmed.length === 0) {
      setShowCharWarning(false);
      setWarningMessage("");
      setHasSearched(false);
      setSearchResults([]);
    } else if (trimmed.length < 3) {
      setShowCharWarning(true);
      setWarningMessage(
        `Make sure to type at least 3 characters to search (${trimmed.length}/3 entered).`
      );
    } else {
      setShowCharWarning(false);
      setWarningMessage("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Backspace") {
      const trimmed = searchTerm.trim();
      if (trimmed.length > 0 && trimmed.length <= 3) {
        setShowCharWarning(true);
        setWarningMessage(
          "⚠️ You backspaced below 3 characters. Make sure you type at least 3 characters to search."
        );
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSearchSubmit(e);
    }
  };

  const handleSearchSubmit = async (e) => {
    if (e) e.preventDefault();
    const trimmed = searchTerm.trim();

    if (trimmed.length < 3) {
      setShowCharWarning(true);
      setWarningMessage(
        "⚠️ Make sure to type at least 3 characters before searching."
      );
      toast.error("Please enter at least 3 characters to start the search.");
      return;
    }

    const schoolId = schoolDetails?.school_id;
    if (!schoolId) {
      toast.error("School information is still loading. Please wait a moment.");
      return;
    }

    setLoading(true);
    setShowCharWarning(false);
    setWarningMessage("");

    try {
      const server_response = await ajaxStudent.searchSchoolStudents(1, schoolId, trimmed);
      setLoading(false);
      setHasSearched(true);
      setSearchedQuery(trimmed);

      if (server_response && server_response.status === "OK") {
        const list = server_response.details?.list || [];
        setSearchResults(list);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      setLoading(false);
      setHasSearched(true);
      setSearchResults([]);
      toast.error("Failed to search students. Please try again.");
    }
  };

  const handleReset = (e) => {
    if (e) e.preventDefault();
    setSearchTerm("");
    setSearchResults([]);
    setHasSearched(false);
    setSearchedQuery("");
    setShowCharWarning(false);
    setWarningMessage("");
  };

  const handleRefresh = (e) => {
    if (e) e.preventDefault();
    if (hasSearched && searchedQuery.length >= 3) {
      handleSearchSubmit(e);
    } else {
      handleReset(e);
    }
  };

  const getDefaultPin = async (e, item) => {
    e.preventDefault();
    const server_response = await ajaxStudent.setDefaultPin(item.user_id);
    if (server_response && server_response.status === "OK") {
      toast.success(server_response.message, { duration: 10000 });
      if (searchedQuery && searchedQuery.length >= 3) {
        try {
          const refreshResponse = await ajaxStudent.searchSchoolStudents(1, schoolDetails.school_id, searchedQuery);
          if (refreshResponse && refreshResponse.status === "OK") {
            setSearchResults(refreshResponse.details?.list || []);
          }
        } catch (err) {
          console.error("Failed to refresh student list after PIN update:", err);
        }
      }
    } else {
      toast.error(server_response?.message || "Failed to set default PIN.");
    }
  };

  return (
    <AppContainer title="Students">
      <Toaster position="top-center" reverseOrder={false} />

      <div className="row">
        <div className="col-lg-12">
          <div className="card custom-card">
            <div className="card-body map-card">
              <div className="heading-layout1 mg-b-25">
                <TableHeader
                  title="School Students"
                  subtitle="Search for registered students in the school."
                />
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle"
                    role="button"
                    data-toggle="dropdown"
                    aria-expanded="false"
                  >
                    ...
                  </Link>

                  <div className="dropdown-menu dropdown-menu-right">
                    <Link className="dropdown-item" onClick={handleRefresh}>
                      <FontAwesomeIcon
                        icon={faRedoAlt}
                        className="text-orange-peel"
                        style={{ marginRight: "8px" }}
                      />
                      Refresh
                    </Link>
                  </div>
                </div>
              </div>

              
              

              {/* Dedicated Search Form */}
              <form onSubmit={handleSearchSubmit} className="mg-t-10 mb-4">
                <div className="row gutters-5 align-items-center">
                  <div className="col-8-xxxl col-lg-8 col-12 form-group mb-2 mb-lg-0">
                    <div
                      style={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center"
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faSearch}
                        style={{
                          position: "absolute",
                          left: "16px",
                          color: "#6c757d",
                          fontSize: "1rem",
                          zIndex: 2,
                          pointerEvents: "none"
                        }}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Search by names or student code..."
                        style={{
                          height: "48px",
                          paddingLeft: "44px",
                          paddingRight: searchTerm ? "40px" : "16px",
                          fontSize: "15px",
                          borderRadius: "30px",
                          border: showCharWarning
                            ? "1.5px solid #ffc107"
                            : "1px solid #ced4da",
                          boxShadow: "none"
                        }}
                      />
                      {searchTerm && (
                        <button
                          type="button"
                          onClick={handleReset}
                          style={{
                            position: "absolute",
                            right: "14px",
                            background: "none",
                            border: "none",
                            color: "#8c98a4",
                            cursor: "pointer",
                            padding: "4px",
                            zIndex: 2
                          }}
                          title="Clear search"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="col-4-xxxl col-lg-4 col-12 form-group mb-0 d-flex">
                    <button
                      type="submit"
                      className="btn-fill-lmd radius-30 text-light shadow-dodger-blue bg-dodger-blue"
                      style={{
                        height: "48px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: 1,
                        fontWeight: 600,
                        cursor: "pointer"
                      }}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faSearch} style={{ marginRight: "8px" }} />
                      SEARCH
                    </button>
                    <button
                      type="button"
                      onClick={handleReset}
                      className="btn-fill-lmd radius-30 text-light shadow-dodger-blue bg-martini ml-2"
                      style={{
                        height: "48px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: "100px",
                        fontWeight: 600,
                        cursor: "pointer"
                      }}
                    >
                      <FontAwesomeIcon icon={faRedoAlt} style={{ marginRight: "6px" }} />
                      RESET
                    </button>
                  </div>
                </div>

                {/* Character Warning & Guidance Indicator */}
                <div className="mt-2" style={{ minHeight: "26px" }}>
                  {showCharWarning ? (
                    <div
                      className="d-flex align-items-center"
                      style={{
                        color: "#856404",
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffeeba",
                        borderRadius: "6px",
                        padding: "6px 14px",
                        fontSize: "0.85rem",
                        display: "inline-flex"
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        style={{ marginRight: "8px", color: "#e0a800" }}
                      />
                      <span>
                        {warningMessage ||
                          "Make sure to type at least 3 characters to search."}
                      </span>
                    </div>
                  ) : searchTerm.trim().length >= 3 ? (
                    <div
                      style={{
                        color: "#28a745",
                        fontSize: "0.85rem",
                        paddingLeft: "8px"
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        style={{ marginRight: "6px" }}
                      />
                      Ready to search ({searchTerm.trim().length} characters). Press
                      Enter or click Search.
                    </div>
                  ) : (
                    <div
                      style={{
                        color: "#6c757d",
                        fontSize: "0.83rem",
                        paddingLeft: "8px"
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        style={{ marginRight: "6px" }}
                      />
                      Enter at least 3 characters. For better results, type more characters
                      of the student's name or code.
                    </div>
                  )}
                </div>
              </form>

              <div className="border-top mt-3 mb-3"></div>

              {/* Main Content Area */}
              {loading ? (
                <div className="text-center py-5">
                  <Loader />
                  <p className="text-muted mt-3 mb-0">Searching students on Page 1...</p>
                </div>
              ) : !hasSearched ? (
                /* Initial Display Guide - No Student List Loaded */
                <div
                  className="text-center p-5 mt-2"
                  style={{
                    backgroundColor: "#fafbfc",
                    border: "1px dashed #d0d7de",
                    borderRadius: "12px"
                  }}
                >
                  <div
                    style={{
                      width: "68px",
                      height: "68px",
                      borderRadius: "50%",
                      backgroundColor: "#e8effa",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "16px"
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faUserGraduate}
                      style={{ fontSize: "1.8rem", color: "#042954" }}
                    />
                  </div>
                  <h4 style={{ fontWeight: 600, color: "#042954", marginBottom: "8px" }}>
                    Search Students
                  </h4>
                  <p
                    style={{
                      maxWidth: "560px",
                      margin: "0 auto 24px auto",
                      color: "#6c757d",
                      fontSize: "0.95rem"
                    }}
                  >
                    To manage student details, enter their first name,
                    last name, or student code in the search bar above.
                  </p>

                  
                </div>
              ) : searchResults.length === 0 ? (
                /* No Results Found State */
                <div
                  className="text-center p-5 mt-2"
                  style={{
                    backgroundColor: "#fafbfc",
                    border: "1px dashed #d0d7de",
                    borderRadius: "12px"
                  }}
                >
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "50%",
                      backgroundColor: "#fde8e8",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "16px"
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faSearch}
                      style={{ fontSize: "1.5rem", color: "#e53e3e" }}
                    />
                  </div>
                  <h4 style={{ fontWeight: 600, color: "#1a202c", marginBottom: "6px" }}>
                    No Students Found
                  </h4>
                  <p
                    style={{
                      color: "#6c757d",
                      fontSize: "0.95rem",
                      maxWidth: "500px",
                      margin: "0 auto 16px auto"
                    }}
                  >
                    No students matching "<strong>{searchedQuery}</strong>" were found in
                    this school.
                  </p>
                 
                  <button
                    type="button"
                    onClick={handleReset}
                    className="btn btn-outline-secondary radius-30 px-4"
                    style={{ fontWeight: 600 }}
                  >
                    Clear Search
                  </button>
                </div>
              ) : (
                /* Search Results Table - Page 1 Only */
                <div>
                  <div
                    className="d-flex flex-wrap justify-content-between align-items-center mb-3 p-1"
                    style={{
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9ecef"
                    }}
                  >
                    <div>
                      <span
                        style={{
                          fontWeight: 600,
                          color: "#042954",
                          fontSize: "0.95rem"
                        }}
                      >
                        Search Results:
                      </span>
                      <span className="text-muted ml-2" style={{fontSize:"0.95rem"}}>
                        Found <strong>{searchResults.length}</strong> matching student
                        {searchResults.length === 1 ? "" : "s"} for "
                        <strong>{searchedQuery}</strong>"
                      </span>
                    </div>
                    
                  </div>

                  

                  <div className="table-responsive">
                    <table className="table display data-table text-nowrap">
                      <thead>
                        <tr>
                          <th style={{ width: "50px" }}>No.</th>
                          <th>Name</th>
                          <th>Gender</th>
                          <th>Student Code</th>
                          <th>Student Group</th>
                          <th>Default Pin</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map((item, index) => (
                          <tr key={item.student_id || index}>
                            <td style={{ width: "50px" }}>{index + 1}</td>
                            <td>
                              <Link
                                to={`/school-students/profile/${item.student_id}/${item.user_id}`}
                                style={{ fontWeight: 600, color: "#042954" }}
                              >
                                {item.full_name}
                              </Link>
                            </td>
                            <td className="text-dark">{item.gender}</td>
                            <td className="text-dark">{item.username}</td>
                            <td className="text-dark">{item.group}</td>
                            <td>
                              {item.is_secure === "1" ? (
                                <span className="badge badge-success">SECURED</span>
                              ) : (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id={`tooltip-${index}`}>
                                      Account is not secure!
                                    </Tooltip>
                                  }
                                >
                                  <span className="badge badge-danger">
                                    {item.default_pin}
                                  </span>
                                </OverlayTrigger>
                              )}
                            </td>
                            <td>
                              <div className="dropdown">
                                <Link
                                  to="#"
                                  className="dropdown-toggle"
                                  data-toggle="dropdown"
                                  aria-expanded="false"
                                >
                                  <span className="flaticon-more-button-of-three-dots"></span>
                                </Link>
                                <div className="dropdown-menu dropdown-menu-right">
                                  <Link
                                    className="dropdown-item"
                                    to="#"
                                    onClick={(e) => getDefaultPin(e, item)}
                                  >
                                    <FontAwesomeIcon
                                      icon={faUserLock}
                                      style={{ color: "teal", marginRight: "6px" }}
                                    />
                                    Set Default Pin
                                  </Link>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppContainer>
  );
}

export default ViewSchoolStudentsPage;
